/**
 * patternForwardStats — Wave G.2 forward-outcome probability engine.
 *
 * Answers the trader's question from 2026-05-18:
 *   "What's the probability we bounce the 5M 09EMA after rejecting off
 *    the Golden Crayon? The 15M 09EMA, 21EMA, etc.?"
 *
 * For a given bars[] history:
 *   1. Walk every bar from `scanFrom` to `bars.length - forwardWindow`
 *   2. At each bar, run all 7 Wave C pattern detectors for BOTH directions
 *      (long + short) — the bar might fire long-side patterns AND nothing
 *      short-side, or vice versa, or both
 *   3. Filter to ACTIONABLE patterns (the same threshold as
 *      counterBiasWhisper — a trader would actually take this)
 *   4. For each actionable fire, evaluate each outcome predicate over the
 *      forward window
 *   5. Aggregate per (patternType, direction) → { totalFires, outcomes }
 *
 * Default outcomes answer "follow-through" and "EMA tag" questions:
 *   - followsThrough3 / 6 / 12  — close N bars later in pattern direction
 *   - tagsEma9_6 / tagsEma21_6  — EMA touched in next 6 bars
 *
 * Caller can pass custom outcomes for bespoke questions (e.g. "does price
 * reach +1R within 12 bars?").
 *
 * Pure function. No side effects. Detector errors are isolated (one
 * failure doesn't poison the rest).
 */

import { detectSetupPatterns } from '../barAnalysis/setupPatterns.js';
import { computeEMA } from '../barAnalysis/emas.js';

// Re-exported from counterBiasWhisper philosophy — keep the actionable
// threshold consistent across surfaces.
const isActionablePattern = (p) => {
  if (!p || !p.type) return false;
  switch (p.type) {
    case 'wolf-stack':         return p.score >= 3;
    case 'signal-bar-quality': return p.score === 3;
    case 'holy-grail':
    case '3-bar-play':
    case 'uno-reverse':
    case 'h2':
    case 'l2':
      return true;
    case 'turtle-soup':        return p.alignsWithTrade === true;
    default: return false;
  }
};

/**
 * Default forward-outcome predicates. Each one takes the precomputed
 * `ctx` (bars + ema series) plus the anchor bar's index and the implied
 * pattern direction, and returns true/false for that anchor.
 *
 * Forward window: each predicate names its own N. The engine doesn't
 * need a global forward window — it just needs `bars.length - i` to be
 * large enough for the longest N (typically 12 bars on 5m = 60 min).
 */
export const DEFAULT_OUTCOMES = {
  followsThrough3: {
    label: 'Closes in direction (15 min)',
    fn: (ctx, i, dir) => closesInDirection(ctx.bars, i, 3, dir),
  },
  followsThrough6: {
    label: 'Closes in direction (30 min)',
    fn: (ctx, i, dir) => closesInDirection(ctx.bars, i, 6, dir),
  },
  followsThrough12: {
    label: 'Closes in direction (60 min)',
    fn: (ctx, i, dir) => closesInDirection(ctx.bars, i, 12, dir),
  },
  tagsEma9_6: {
    label: 'Tags 9 EMA (30 min)',
    fn: (ctx, i) => tagsEmaWithin(ctx.bars, ctx.ema9, i, 6),
  },
  tagsEma21_6: {
    label: 'Tags 21 EMA (30 min)',
    fn: (ctx, i) => tagsEmaWithin(ctx.bars, ctx.ema21, i, 6),
  },
};

// ── outcome predicate helpers ────────────────────────────────────

/**
 * True when the close N bars after the anchor is in the pattern direction
 * relative to the anchor's close.
 */
const closesInDirection = (bars, anchorIdx, n, dir) => {
  const anchor = bars[anchorIdx];
  const target = bars[anchorIdx + n];
  if (!anchor || !target) return false;
  const ac = Number(anchor.close);
  const tc = Number(target.close);
  if (!Number.isFinite(ac) || !Number.isFinite(tc)) return false;
  return dir === 'long' ? tc > ac : tc < ac;
};

/**
 * True when any bar within [anchorIdx+1, anchorIdx+n] has its [low, high]
 * range containing the EMA value at that bar's index (i.e., price touched
 * the EMA at least once in the forward window).
 */
const tagsEmaWithin = (bars, emaSeries, anchorIdx, n) => {
  for (let k = anchorIdx + 1; k <= anchorIdx + n && k < bars.length; k++) {
    const b = bars[k];
    const e = emaSeries[k]?.value;
    if (b == null || e == null) continue;
    const lo = Number(b.low);
    const hi = Number(b.high);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) continue;
    if (lo <= e && e <= hi) return true;
  }
  return false;
};

// ── main engine ──────────────────────────────────────────────────

/**
 * @param {Object} params
 * @param {Array}  params.bars            5m bars array (sorted ascending by time).
 * @param {Object} [params.outcomes]      Map of outcome predicates. See DEFAULT_OUTCOMES.
 * @param {number} [params.scanFrom=60]   First bar index to scan (must be ≥ detector warm-up,
 *                                        ~60 bars covers all 7 detectors safely).
 * @param {number} [params.scanTo]        Last bar index to scan (defaults to
 *                                        bars.length - longestOutcomeWindow). Inclusive.
 * @param {number} [params.longestOutcomeWindow=12]  Bars of forward room needed; used to
 *                                                   derive scanTo when not provided.
 * @param {Array<string>} [params.directions=['long','short']]  Directions to fire detectors with.
 * @param {Function} [params.patternFilter]  Function (pattern) => boolean. Defaults to
 *                                           isActionablePattern (the same threshold the
 *                                           counter-bias whisper uses).
 *
 * @returns {Object}
 *   {
 *     totalScanned,                    // bars actually scanned
 *     scanFrom, scanTo,                // bounds used
 *     results: {
 *       'wolf-stack:long': {
 *         patternType, direction, totalFires,
 *         outcomes: {
 *           followsThrough6: { fires: 12, sample: 18, probability: 0.667 },
 *           ...
 *         }
 *       },
 *       ...
 *     }
 *   }
 *
 *   Note: `sample` per outcome may be smaller than `totalFires` if that
 *   particular outcome couldn't be evaluated for some anchors (e.g.,
 *   EMA still in warm-up for that bar).
 */
export const analyzePatternForwardStats = ({
  bars,
  outcomes = DEFAULT_OUTCOMES,
  scanFrom = 60,
  scanTo,
  longestOutcomeWindow = 12,
  directions = ['long', 'short'],
  patternFilter = isActionablePattern,
} = {}) => {
  if (!Array.isArray(bars) || bars.length === 0) {
    return { totalScanned: 0, scanFrom: 0, scanTo: 0, results: {} };
  }

  const safeFrom = Math.max(scanFrom, 0);
  const safeTo = Math.min(
    scanTo ?? (bars.length - 1 - longestOutcomeWindow),
    bars.length - 1
  );
  if (safeFrom > safeTo) {
    return { totalScanned: 0, scanFrom: safeFrom, scanTo: safeTo, results: {} };
  }

  // Precompute EMAs once. Defaults match the outcomes in DEFAULT_OUTCOMES.
  // If the caller passes custom outcomes that need other EMAs, they can
  // build their own ctx in their predicate (this is a starter set).
  const ctx = {
    bars,
    ema9: computeEMA(bars, 9),
    ema21: computeEMA(bars, 21),
    ema50: computeEMA(bars, 50),
  };

  // Result accumulator keyed by `${patternType}:${direction}`.
  const results = {};
  const ensureBucket = (patternType, direction) => {
    const key = `${patternType}:${direction}`;
    if (!results[key]) {
      results[key] = {
        patternType,
        direction,
        totalFires: 0,
        outcomes: {},
      };
      for (const name of Object.keys(outcomes)) {
        results[key].outcomes[name] = { fires: 0, sample: 0, probability: 0 };
      }
    }
    return results[key];
  };

  let totalScanned = 0;
  for (let i = safeFrom; i <= safeTo; i++) {
    const anchor = bars[i];
    if (!anchor) continue;
    const entryTs = Number(anchor.time);
    const entryPrice = Number(anchor.close);
    if (!Number.isFinite(entryTs) || !Number.isFinite(entryPrice)) continue;
    totalScanned++;

    for (const dir of directions) {
      let patterns;
      try {
        patterns = detectSetupPatterns({
          bars,
          entryTs,
          entryPrice,
          direction: dir,
        });
      } catch {
        continue;
      }
      if (!Array.isArray(patterns) || patterns.length === 0) continue;

      const actionable = patterns.filter(patternFilter);
      for (const p of actionable) {
        const bucket = ensureBucket(p.type, dir);
        bucket.totalFires++;
        for (const [name, spec] of Object.entries(outcomes)) {
          let fired;
          try {
            fired = spec.fn(ctx, i, dir);
          } catch {
            fired = null;  // predicate failure → don't count in sample
          }
          if (fired === null || fired === undefined) continue;
          bucket.outcomes[name].sample++;
          if (fired) bucket.outcomes[name].fires++;
        }
      }
    }
  }

  // Finalize probabilities.
  for (const bucket of Object.values(results)) {
    for (const o of Object.values(bucket.outcomes)) {
      o.probability = o.sample > 0 ? o.fires / o.sample : 0;
    }
  }

  return {
    totalScanned,
    scanFrom: safeFrom,
    scanTo: safeTo,
    results,
  };
};

// Test/inspection exports
export { isActionablePattern, closesInDirection, tagsEmaWithin };
